import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Escaner por camara. La API de camara del navegador solo funciona en
// contexto seguro (HTTPS o localhost); en la LAN por HTTP queda bloqueada,
// asi que se avisa y se cae al ingreso manual del codigo.
// Que el contexto sea seguro no es un ESTADO: no cambia mientras la
// pantalla está abierta, así que se deriva en el render. Guardarlo con
// setState dentro del efecto encadenaba un render de más y React lo
// señalaba con razón.
const AVISO_INSEGURO =
  'La cámara necesita una conexión segura. Si ves un aviso de certificado, '
  + 'instala el certificado de TEGO en este dispositivo. Mientras tanto, '
  + 'teclea el código o usa un lector USB.';

export default function EscanerCamara({ onCodigo, onCerrar }) {
  const ref = useRef(null);
  const scannerRef = useRef(null);
  const [errorCamara, setErrorCamara] = useState(null);
  const seguro = window.isSecureContext;
  const error = seguro ? errorCamara : AVISO_INSEGURO;

  useEffect(() => {
    if (!seguro) return undefined;
    let cancelado = false;
    // Además del QR propio, los códigos que trae el producto de fábrica.
    // Sin esta lista, la cámara solo entiende QR y hay que reetiquetar
    // cada artículo antes de poder buscarlo.
    const scanner = new Html5Qrcode('lector-qr', {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.CODE_128,
        // CATÁLOGO VIVO F5.3 (R-CV3): la bolsa de Digi-Key, Mouser o Steren trae
        // un Data Matrix con el número de parte y la cantidad.
        Html5QrcodeSupportedFormats.DATA_MATRIX,
      ],
    });
    scannerRef.current = scanner;
    // La caja se guarda ahora: al desmontar, React ya soltó la referencia.
    const caja = ref.current;

    const arranque = scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (texto) => {
          if (cancelado) return;
          cancelado = true;
          scanner.stop().catch(() => {}).finally(() => onCodigo(texto));
        },
        () => {},
      )
      .catch(() => setErrorCamara('No se pudo abrir la cámara. Revisa los permisos o teclea el código.'));

    // AL CERRAR, LA CÁMARA SE APAGA SIEMPRE (TC-DR-AT, corrección de un
    // defecto). Antes solo se paraba si ya estaba leyendo: quien cerraba
    // justo después de abrir —`start()` todavía pendiente— dejaba el
    // flujo de video encendido, con la luz de la cámara prendida. Ahora
    // se espera a que el arranque termine, se para, y además se apaga
    // cada pista de video que hubiera quedado en la caja.
    return () => {
      cancelado = true;
      const pistas = () => [...(caja?.querySelectorAll('video') || [])]
        .flatMap((v) => v.srcObject?.getTracks?.() || []);
      const antes = pistas();
      Promise.resolve(arranque)
        .then(() => (scanner.isScanning ? scanner.stop() : undefined))
        .catch(() => {})
        .finally(() => { [...antes, ...pistas()].forEach((t) => t.stop()); });
    };
  }, [onCodigo, seguro]);

  return (
    <div className="space-y-2">
      <div id="lector-qr" ref={ref} className="rounded-xl overflow-hidden bg-black" />
      {error && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5">
          {error}
        </div>
      )}
      <button
        onClick={onCerrar}
        className="w-full min-h-11 text-sm font-semibold text-gray-500 hover:text-gray-800 py-2"
      >
        Cerrar cámara
      </button>
    </div>
  );
}
